import React, { useState, useEffect, useMemo } from "react";
import styles from "./CoreModules.module.css";
import { CircleMinus, CirclePlus } from "lucide-react";

interface SemesterItem {
  id: number;
  name: string;
  credits: string;
}

interface Semester {
  id: number;
  heading_text: string;
  credit_text: string;
  items: SemesterItem[];
}

interface YearModule {
  id: number;
  year_title: string;
  semesters: Semester[];
}

interface CoreModulesProps {
  data:
    | {
        id: number;
        title: string;
        sub_title: string;
        core_module_list: YearModule[];
      }
    | YearModule[]; // For isLpPage
  isLpPage?: boolean;
}

const CoreModules: React.FC<CoreModulesProps> = ({ data, isLpPage }) => {
  const moduleList: YearModule[] = useMemo(() => 
    isLpPage
      ? (data as YearModule[])
      : (data as any)?.core_module_list ?? [],
    [data, isLpPage]
  );

  const [openYears, setOpenYears] = useState<boolean[]>([]);

  useEffect(() => {
    if (moduleList && moduleList.length) {
      setOpenYears(moduleList.map((_, index) => index === 0));
    }
  }, [moduleList]);

  if (!moduleList.length) return null;

  const toggleYear = (index: number) => {
    setOpenYears((prev) => {
      const newOpen = [...prev];
      newOpen[index] = !newOpen[index];
      return newOpen;
    });
  };

  const formatTitle = (text: string) =>
    text?.split(/(\d+)/)?.map((part, index) =>
      /^\d+$/.test(part) ? (
        <span key={index} className={styles.numberText}>
          {part}
        </span>
      ) : (
        <span key={index} className={styles.text}>
          {part}
        </span>
      )
    );

  return (
    <div className={styles.coreModules}>
      {!isLpPage && (
        <>
          <h2>{formatTitle((data as any)?.title)}</h2>
          <p>{(data as any)?.sub_title}</p>
        </>
      )}

      {moduleList.map((yearData, index) => (
        <div key={yearData?.id}>
          <div className={styles.yearHeader} onClick={() => toggleYear(index)}>
            <h3>{formatTitle(yearData?.year_title)}</h3>
            {openYears[index] ? <CircleMinus /> : <CirclePlus />}
          </div>

          {openYears[index] && (
            <div className={styles.yearContent}>
              {yearData?.semesters?.map((semester) => (
                <div key={semester?.id} className={styles.semesterBlock}>
                  {(semester?.heading_text || semester?.credit_text) && (
                    <div className={styles.semesterHeader}>
                      <div>
                        <span className={styles.text}>
                          {semester?.heading_text?.replace(/\d+/g, "").trim()}
                        </span>
                        <span className={styles.number}>
                          {semester?.heading_text?.match(/\d+/)}
                        </span>
                      </div>
                      <span className={styles.Credits}>
                        {semester?.credit_text}
                      </span>
                    </div>
                  )}

                  <div className={styles.modulesList}>
                    {semester?.items?.map((module) => (
                      <div key={module?.id} className={styles.moduleItem}>
                        <span>{module?.name}</span>
                        <span>{module?.credits}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default CoreModules;
